import React, { useEffect, useMemo } from "react";
import { Input } from "antd";
import PreviewQuestionLabel from "../../../common/PreviewQuestionLabel/PreviewQuestionLabel";
import { useSurveyInputStore } from "../../../../surveyStore";
import PreviewQuestionConstraints from "../../../common/PreviewQuestionConstraints/PreviewQuestionConstraints";
import useEvaluateConstraint from "../../question-preview/hooks/useEvaluateConstraint";
import useEvaluateAppearance from "../../question-preview/hooks/useEvaluateAppearance";
import useGetOrderedSurveys from "../../../../editor/logic-builder/hooks/useGetOrderedSurveys";
import { REGEX } from "../../../../../../../helpers/constraintParsers";

import "./Text.scss";

const { TextArea } = Input;

export const Text = React.memo(
   ({ props, hasErrors, surveyId, userId, submissionId, label, hint, language, defaultValue }) => {
      const { id, questionType, isRequired, answers } = props;
      const inputState = useSurveyInputStore((state) => state.inputs?.[id]);
      const setInputs = useSurveyInputStore((state) => state.setInputs);
      const orderedSurveys = useGetOrderedSurveys();

      const hasSubmissionId = Boolean(submissionId);

      const jrChoiceNameMatch = props.calculation && props.calculation.match(REGEX.jrChoiceName);
      const hasChoiceNameRegex = jrChoiceNameMatch && jrChoiceNameMatch?.[0] !== "";

      const choiceNameOptions = useMemo(() => {
         if (!hasChoiceNameRegex) return [];
         if (!orderedSurveys?.surveyOrdered || !jrChoiceNameMatch?.[2]) return [];

         const searchName = jrChoiceNameMatch[2].replace(/\$|{|}/g, "");
         const surveyItem = orderedSurveys.surveyOrdered?.find((item) => {
            const matches = item.surveyItem?.name === searchName;
            return matches;
         });
         if (!surveyItem) return [];

         return surveyItem.surveyItem.selectQuestionChoices?.map((item) => {
            const translatedLabel = item.choiceLabels.find((label) => label.languageId === language)
               ?.text;
            const fallbackLabel = item.choiceLabels[0]?.text;

            return {
               label: translatedLabel || fallbackLabel,
               value: item.name
            };
         });
      }, [hasChoiceNameRegex, orderedSurveys, jrChoiceNameMatch, language]);

      const evaluateConstraint = useEvaluateConstraint(
         jrChoiceNameMatch?.[0],
         true,
         choiceNameOptions
      );

      const submissionAnswer = hasSubmissionId
         ? answers?.find((answer) =>
              props.repeatInstanceIndex
                 ? answer?.answerDetail.repeatGroup === props.repeatInstanceIndex
                 : answer
           )?.answerDetail?.text
         : null;

      const value = hasSubmissionId ? submissionAnswer : inputState?.answerDetail?.text ?? null;
      const passConstraint = useEvaluateConstraint(
         !hasSubmissionId ? props.constraint : null,
         value
      );

      const appearance = useEvaluateAppearance({
         appearance: props.appearance
      });

      const showErrors = hasErrors && isRequired && !inputState?.answerDetail?.text;

      const onChange = (event) => {
         if (hasSubmissionId) return;

         const inputValue = event.target.value;

         if (appearance.includes("numbers")) {
            if (!inputValue) {
               setInputs({
                  target: {
                     name: id,
                     value: ""
                  }
               });
            } else {
               setInputs({
                  target: {
                     name: id,
                     value: {
                        surveyId: surveyId,
                        userId: userId,
                        questionId: id,
                        questionType: questionType,
                        name: props.name,
                        skipLogicAnswer: inputValue,
                        answerDetail: {
                           text: inputValue
                        }
                     }
                  }
               });
            }
         } else {
            if (!inputValue || inputValue.length === 0) {
               setInputs({
                  target: {
                     name: id,
                     value: ""
                  }
               });
            } else {
               setInputs({
                  target: {
                     name: id,
                     value: {
                        surveyId: surveyId,
                        userId: userId,
                        questionId: id,
                        questionType: questionType,
                        name: props.name,
                        skipLogicAnswer: inputValue,
                        answerDetail: {
                           text: inputValue
                        }
                     }
                  }
               });
            }
         }
      };

      useEffect(() => {
         const textValue = evaluateConstraint ?? (props.default || defaultValue || value);

         onChange({ target: { value: textValue } });
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [props.default, defaultValue, evaluateConstraint]);

      const onKeyDown = (event) => {
         if (event.key === "." || event.key === ",") {
            event.preventDefault();
         }
      };

      return (
         <div className="textarea-submission-type-wrapper">
            <PreviewQuestionLabel isRequired={isRequired} hint={hint} label={label} />

            {appearance.includes("numbers") ? (
               <Input
                  size="large"
                  type="number"
                  name={id}
                  min={0}
                  step={1}
                  onKeyDown={onKeyDown}
                  readOnly={hasSubmissionId}
                  status={showErrors || (passConstraint !== null && !passConstraint) ? "error" : ""}
                  placeholder={"Type your answer here"}
                  value={value}
                  onChange={onChange}
                  prefixCls="number-submission-type-wrapper--number"
                  style={{
                     padding: "7px 11px"
                  }}
               />
            ) : (
               <TextArea
                  rows={4}
                  required={isRequired}
                  status={showErrors || (passConstraint !== null && !passConstraint) ? "error" : ""}
                  placeholder="Type your answer here"
                  readOnly={hasSubmissionId}
                  name={id}
                  value={value}
                  onChange={onChange}
                  className="textarea-submission-type-wrapper--textarea"
                  maxLength={30000}
               />
            )}
            <span className="textarea-submission-type-wrapper--error">
               {showErrors ? "This field is required" : " "}
               {passConstraint !== null && !passConstraint ? (
                  <PreviewQuestionConstraints constraintMessages={props.constraintMessages} />
               ) : null}
            </span>
         </div>
      );
   }
);
